/**
 * PreviewTarget — ONE normalized shape behind the universal file preview
 * (founder ruling 08-03: any file in any folder → click → full-screen
 * in-place preview + an open-in-new-tab escape hatch, one grammar for every
 * type). Each grid maps its own row shape into this before opening.
 *
 * URL semantics (hardened after the 08-03 founder round):
 *  - `viewUrl` = same-origin `/v/<id>` proxy. ONLY minted where that id-space
 *    actually serves: user files (uuid ids) and html-ish artifacts. Media
 *    artifacts open their SIGNED CDN URL directly — pointing a media id at
 *    /v produced "Nothing here" dead tabs.
 *  - `mediaUrl` = directly renderable URL for img/video/audio/pdf elements.
 *  - `downloadHref` = a URL that downloads with the REAL filename: /v gets
 *    `?download=1` (the route flips disposition + carries the name), signed
 *    storage URLs get Supabase's `download=<filename>` param. A bare signed
 *    URL + the HTML download attribute saved cross-origin files as `.bin`.
 */
import type { UserFile } from "@bash/shared";
import { fileViewerUrl } from "@/lib/files-urls";
import { briefTitle } from "@/lib/creations/creationTitle";
import {
  artifactDownloadHref,
  extFromMime,
  nameFromUrl,
  withDownloadName,
} from "@/lib/artifact-download";
import {
  kindFromFilename,
  kindFromArtifact,
  type FileKind,
} from "./FileTileCaption";

/** Share identity: the id the share dialog opens, and which lane it lives in. */
export interface PreviewShare {
  lane: "user-file" | "artifact";
  id: string;
}

/** The file's story for the popup header — every field optional; the header
 *  renders only what the row actually knows. `origin` phrases the date line:
 *  'agent' → "Created ‹date› by Bash", 'user' → "Added ‹date›". */
export interface PreviewMeta {
  sizeBytes?: number | null;
  createdAt?: string | null;
  origin?: "agent" | "user";
}

export interface PreviewTarget {
  name: string;
  kind: FileKind;
  /** Lowercase filename extension ('' when none) — drives per-format rendering. */
  ext: string;
  mediaUrl: string | null;
  viewUrl: string | null;
  downloadHref: string | null;
  /** Present when the file can be shared from the overlay. */
  share?: PreviewShare;
  meta?: PreviewMeta;
  /** Real pixel dimensions when known (rehost's dimension probe) — reserves
   *  the stage's box before the image/video loads so nothing jumps. */
  width?: number | null;
  height?: number | null;
  /** The ratio REQUESTED at generation time, never measured — the CLS
   *  fallback when width/height aren't known yet. */
  aspect?: string | null;
}

function extOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot + 1).toLowerCase();
}

/** Last-resort display name: the object's basename inside a storage URL —
 *  old artifact rows predate captions/filenames and read "untitled" without it.
 *  Re-exported from the shared download rule, which owns the same parse. */
export { nameFromUrl };

export function targetFromUserFile(
  file: Pick<UserFile, "filename" | "downloadUrl"> & {
    id?: string;
    sizeBytes?: number | null;
    createdAt?: string;
    source?: UserFile["source"];
    width?: number | null;
    height?: number | null;
  }
): PreviewTarget {
  const viewUrl = fileViewerUrl(file.downloadUrl);
  return {
    name: file.filename,
    kind: kindFromFilename(file.filename),
    ext: extOf(file.filename),
    mediaUrl: file.downloadUrl ?? viewUrl,
    viewUrl,
    ...(file.id
      ? {
          share: { lane: "user-file" as const, id: file.id },
        }
      : {}),
    downloadHref: viewUrl
      ? `${viewUrl}?download=1`
      : file.downloadUrl
        ? withDownloadName(file.downloadUrl, file.filename)
        : null,
    // Real measured dimensions once the file-thumb render lane has probed
    // them (image: sharp metadata; video: ffprobe) — no `aspect` fallback to
    // set here, since uploads have no "requested ratio" the way a generation
    // intent does; LoadedImage/LoadedVideo just skip the box reservation
    // until these land.
    width: file.width ?? null,
    height: file.height ?? null,
    meta: {
      sizeBytes: file.sizeBytes ?? null,
      createdAt: file.createdAt ?? null,
      ...(file.source ? { origin: file.source } : {}),
    },
  };
}

/** Scoped folder rows (FilesListItem-shaped: title + url) — task outputs,
 *  so the origin is always the agent. */
export function targetFromListRow(row: {
  title: string;
  url?: string | null;
  id?: string;
  createdAt?: string;
}): PreviewTarget {
  const viewUrl = fileViewerUrl(row.url ?? null);
  return {
    name: row.title,
    kind: kindFromFilename(row.title),
    ext: extOf(row.title),
    mediaUrl: row.url ?? viewUrl,
    viewUrl,
    ...(row.id
      ? {
          share: { lane: "user-file" as const, id: row.id },
        }
      : {}),
    downloadHref: viewUrl
      ? `${viewUrl}?download=1`
      : row.url
        ? withDownloadName(row.url, row.title)
        : null,
    meta: { createdAt: row.createdAt ?? null, origin: "agent" },
  };
}

/** A page made of text the app already holds — a long reply opened as a page
 *  (founder 08-31: "open the computer with this as md file preview"). A
 *  `data:` URL is a real URL to the preview's fetch, so the .md lane renders
 *  it and Download saves it under its name; no file is minted until someone
 *  asks for one, so opening twice never makes two. No /v id, no share lane. */
export function targetFromMarkdown(page: {
  name: string;
  text: string;
}): PreviewTarget {
  const name = extOf(page.name) === "md" ? page.name : `${page.name}.md`;
  const url = `data:text/markdown;charset=utf-8,${encodeURIComponent(page.text)}`;
  return {
    name,
    kind: kindFromFilename(name),
    ext: "md",
    mediaUrl: url,
    viewUrl: null,
    downloadHref: url,
    meta: {
      sizeBytes: new TextEncoder().encode(page.text).length,
      origin: "agent",
    },
  };
}

/** Artifacts (Media folder + Creations): `id` is the /v public id, but /v is
 *  only minted for html-ish kinds — media opens its signed URL directly. */
export function targetFromArtifact(a: {
  id: string;
  url: string;
  kind: string;
  subkind?: string | null;
  filename?: string | null;
  caption?: string | null;
  brief?: string | null;
  created_at?: string;
  size_bytes?: number | null;
  width?: number | null;
  height?: number | null;
  aspect?: string | null;
  mime_type?: string | null;
}): PreviewTarget {
  // Brief-derived title BEFORE the url basename: a generated-media row has no
  // caption/filename and its storage object is `<jobId>.bin` — a uuid header.
  const name =
    a.caption ||
    a.filename ||
    briefTitle(a.brief) ||
    nameFromUrl(a.url) ||
    "File";
  const kind = kindFromArtifact(a);
  const htmlish = kind === "report" || kind === "page";
  // Generated media's display name is prose (brief-derived) and carries no
  // extension, so the renderer borrows the one the storage object actually has
  // ('bin' excluded — it is the absence of a type, not a format): a legacy
  // storage url's basename, else the row's mime type (a /v door has no basename).
  // The DOWNLOAD name applies the same rule; @/lib/artifact-download owns it for
  // every surface with a Download control.
  const storedExt = (() => {
    const base = nameFromUrl(a.url);
    const e = base ? extOf(base) : extFromMime(a.mime_type);
    return e && e !== "bin" ? e : "";
  })();
  return {
    name,
    kind,
    ext: extOf(a.filename || name) || storedExt,
    mediaUrl: a.url || null,
    viewUrl: htmlish && a.id ? `/v/${a.id}` : null,
    ...(a.id ? { share: { lane: "artifact" as const, id: a.id } } : {}),
    downloadHref: artifactDownloadHref(a),
    width: a.width ?? null,
    height: a.height ?? null,
    aspect: a.aspect ?? null,
    meta: {
      sizeBytes: a.size_bytes ?? null,
      createdAt: a.created_at ?? null,
      origin: "agent",
    },
  };
}
